import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Rocket, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { STARTUP } from '@/constants/testIds';

const INDUSTRIES = ['FinTech','HealthTech','ClimateTech','AI/ML','Enterprise SaaS','ConsumerTech','DeepTech','Marketplace','Hardware','EdTech','Other'];
const STAGES = ['idea','seed','series_a','series_b','growth'];

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + submit effects. */
export default function Startups() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name:'', tagline:'', industry:'AI/ML', stage:'seed', website:'', founded_year:'', team_size:'', location:'',
  });

  const load = async () => {
    try {
      const { data } = await api.get('/startups/');
      setItems(data);
    } catch {
      toast.error('Could not load startups');
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        founded_year: form.founded_year ? parseInt(form.founded_year) : null,
        team_size: form.team_size ? parseInt(form.team_size) : null,
      };
      await api.post('/startups/', payload);
      toast.success('Startup created.');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create');
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Startups</div>
          <h1 className="display text-3xl md:text-4xl mt-2">Vet, partner, scale.</h1>
          <p className="text-sm text-slate-600 mt-2">All vetted and in-pipeline ventures in one workspace.</p>
        </div>
        {(user?.role !== 'investor') && (
          <button onClick={() => setOpen(true)} data-testid={STARTUP.createBtn} className="ve-btn-primary self-start">
            <Plus size={14}/> Add Startup
          </button>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && (
          <div className="ve-card p-10 text-center md:col-span-3">
            <Rocket size={28} className="mx-auto text-slate-300 mb-3"/>
            <h3 className="display text-xl">No startups yet</h3>
            <p className="text-sm text-slate-600 mt-2">Add a startup to begin AI vetting, NDA/MOU and investor matching.</p>
          </div>
        )}
        {items.map((s) => (
          <Link key={s.startup_id} to={`/dashboard/startups/${s.startup_id}`} className="ve-card p-6 group transition-all hover:-translate-y-1 hover:shadow-lg hover:border-[#8B3FC5]/30">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{s.industry}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-[0.15em] ${
                s.vetting_status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                s.vetting_status === 'rejected' ? 'bg-rose-50 text-rose-700' :
                'bg-amber-50 text-amber-700'}`}>{s.vetting_status}</span>
            </div>
            <h3 className="display text-xl mt-3 group-hover:text-[#8B3FC5] transition-colors">{s.name}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{s.tagline}</p>
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <span className="capitalize">{s.stage.replace('_',' ')} · {s.location || '—'}</span>
              <span className="display text-base text-[#8B3FC5]">{s.ai_score ? `${Math.round(s.ai_score)} AI` : 'no score'}</span>
            </div>
            <div className="mt-4 ve-btn-ghost text-xs">Open <ArrowRight size={12}/></div>
          </Link>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <form onSubmit={submit} className="ve-card p-7 w-full max-w-2xl bg-white" data-testid="startup-create-modal">
            <div className="flex items-center justify-between">
              <h2 className="display text-2xl">New Startup</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 md:col-span-2">Name
                <input required value={form.name} onChange={e => setForm({...form, name:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 md:col-span-2">Tagline
                <input required value={form.tagline} onChange={e => setForm({...form, tagline:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Industry
                <select value={form.industry} onChange={e => setForm({...form, industry:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Stage
                <select value={form.stage} onChange={e => setForm({...form, stage:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  {STAGES.map(i => <option key={i} value={i}>{i.replace('_',' ')}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Founded
                <input type="number" value={form.founded_year} onChange={e => setForm({...form, founded_year:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Team Size
                <input type="number" value={form.team_size} onChange={e => setForm({...form, team_size:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Location
                <input value={form.location} onChange={e => setForm({...form, location:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Website
                <input value={form.website} onChange={e => setForm({...form, website:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" data-testid={STARTUP.createSubmit} className="ve-btn-primary">Create startup</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
